import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { newSelect as Select } from '../ui/newSelect';
import ChoosePaymentMethods from '../Payments/RedirectToCheckout';
import { Actions as PAYMENTACTIONS } from '../../store/actions/paymentActions';
import {
  getTrialDaysRemaining,
  isActiveTrial,
  isExpiredTrial,
  trialRegistrationErrorMessage,
} from '../utils/subscriptionAccess';
import {
  buildPlanIncludeBullets,
  formatLicensePrice,
  midFteForBand,
  planCta,
} from '../utils/smePlans';

const PROMO_STORAGE_KEY = 'cyf.planPromoCode';

const getInitialPromoCode = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('promoCode') || params.get('coupon') || sessionStorage.getItem(PROMO_STORAGE_KEY) || '';
};

const PlansPage = ({
  orgDetails,
  isWhiteLabelApp,
  plans,
  fteBands,
  licensePrices,
  plansLoading,
  plansError,
  catalogPromo,
  getRegistrationPlans,
  setPlanUpgradeSelection,
  contactSales,
  stripeCouponClear,
}) => {
  const [fteBand, setFteBand] = useState(orgDetails?.fteBand || null);
  const [fte, setFte] = useState(orgDetails?.fte ?? null);
  const [termMonths, setTermMonths] = useState(12);
  const [promoInput, setPromoInput] = useState(getInitialPromoCode);
  const [appliedPromoCode, setAppliedPromoCode] = useState(getInitialPromoCode);
  const [promoMessage, setPromoMessage] = useState('');
  const [showCheckout, setShowCheckout] = useState(false);
  const [contactingPlanId, setContactingPlanId] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const isWhiteLabelContext =
    isWhiteLabelApp || String(orgDetails?.whiteLabelId || '0') !== '0';

  useEffect(() => {
    if (isWhiteLabelContext) return;
    (async () => {
      try {
        const data = await getRegistrationPlans(appliedPromoCode || null);
        if (appliedPromoCode && data?.promo) {
          sessionStorage.setItem(PROMO_STORAGE_KEY, data.promo.code || appliedPromoCode);
          setPromoMessage('');
        } else if (appliedPromoCode) {
          sessionStorage.removeItem(PROMO_STORAGE_KEY);
          setPromoMessage('This promo code is invalid or inactive.');
        }
        setLoadError(null);
      } catch (e) {
        setLoadError(e.code || 'plans_error');
      }
    })();
  }, [isWhiteLabelContext, getRegistrationPlans, appliedPromoCode]);

  useEffect(() => {
    if (orgDetails?.fteBand && !fteBand) setFteBand(orgDetails.fteBand);
    if (orgDetails?.fte != null && fte == null) setFte(orgDetails.fte);
  }, [orgDetails, fteBand, fte]);

  const fteOptions = useMemo(
    () =>
      (fteBands || []).map((b) => ({
        value: b.id,
        label: b.label || b.id,
      })),
    [fteBands]
  );

  const onTrial = isActiveTrial(orgDetails);
  const expired = isExpiredTrial(orgDetails);
  const daysLeft = getTrialDaysRemaining(orgDetails);
  const convertTrial = onTrial || expired || orgDetails?.subscriptionStatus === 'trialing';

  const handleFteBandChange = (value) => {
    setFteBand(value);
    setFte(midFteForBand(value, fteBands));
  };

  const handleApplyPromo = async () => {
    const code = promoInput?.trim();
    if (!code) return;
    setPromoMessage('');
    setAppliedPromoCode(code);
  };

  const handleClearPromo = async () => {
    sessionStorage.removeItem(PROMO_STORAGE_KEY);
    setPromoInput('');
    setAppliedPromoCode('');
    setPromoMessage('');
    stripeCouponClear();
  };

  const handleContactSales = async (plan) => {
    setContactingPlanId(plan.id);
    try {
      await contactSales({
        planId: plan.id,
        fteBand,
        fte,
        message: `In-app interest in ${plan.name}`,
      });
    } catch {
      /* alert already shown */
    } finally {
      setContactingPlanId(null);
    }
  };

  const handleCheckout = (plan) => {
    setPlanUpgradeSelection({
      planId: plan.id,
      fteBand: fteBand || null,
      fte: fte ?? null,
      billingInterval: 'annual',
      lengthMonths: termMonths,
      convertTrial: !!convertTrial,
      promoCode: catalogPromo ? (catalogPromo.code || appliedPromoCode) : null,
    });
    setShowCheckout(true);
  };

  if (isWhiteLabelContext || loadError === 'plans_not_available' || plansError === 'plans_not_available') {
    return (
      <div className='p-6'>
        <Card className='bg-card text-card-foreground max-w-xl'>
          <CardHeader>
            <CardTitle>Plans not available</CardTitle>
          </CardHeader>
          <CardContent className='space-y-3 text-muted-foreground'>
            <p>
              {trialRegistrationErrorMessage(
                'plans_not_available',
                'Self-serve SME plans are only available on Compare Your Footprint direct accounts.'
              )}
            </p>
            <Button asChild variant='outline'>
              <Link to='/'>Back to dashboard</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (showCheckout) {
    return (
      <ChoosePaymentMethods
        renewOrgSub
        setShowPaymentModal={() => {
          setShowCheckout(false);
          setPlanUpgradeSelection(null);
        }}
      />
    );
  }

  return (
    <div className='p-6 space-y-6'>
      <div className='max-w-5xl space-y-2'>
        <h1 className='text-2xl font-semibold text-foreground'>Choose a plan</h1>
        {onTrial && (
          <p className='text-muted-foreground'>
            {daysLeft != null && daysLeft > 0
              ? `You have ${daysLeft} day${daysLeft === 1 ? '' : 's'} left on your free trial.`
              : 'Your free trial is active.'}{' '}
            Upgrade to Measure, Execute, or Coordinate — Discover and Govern are sales-assisted unless you have an unlock
            code.
          </p>
        )}
        {expired && (
          <p className='text-destructive'>
            Your free trial has ended. Choose a plan to restore access. There is no grace period on trials.
          </p>
        )}
        {!onTrial && !expired && (
          <p className='text-muted-foreground'>
            Select a plan and company size. Self-serve Checkout is available for Measure, Execute, and Coordinate.
          </p>
        )}
      </div>

      <Card className='bg-card text-card-foreground max-w-5xl'>
        <CardHeader className='pb-3'>
          <CardTitle className='text-base'>Company size & billing</CardTitle>
          <CardDescription>Price is the License Price for your plan and FTE band. Term is 12 months, or 36 months at 5% off. Notice is 180 days.</CardDescription>
        </CardHeader>
        <CardContent className='flex flex-wrap gap-4 items-end'>
          <div className='w-56'>
            <label className='text-sm text-muted-foreground mb-1 block'>Staff (FTE band)</label>
            <Select
              options={fteOptions}
              value={fteBand}
              onChange={handleFteBandChange}
              placeholder='Select FTE band'
              className='w-full'
            />
          </div>
          <div className='flex gap-2'>
            <Button
              type='button'
              size='sm'
              variant={termMonths === 12 ? 'default' : 'outline'}
              onClick={() => setTermMonths(12)}>
              12 months
            </Button>
            <Button
              type='button'
              size='sm'
              variant={termMonths === 36 ? 'default' : 'outline'}
              onClick={() => setTermMonths(36)}>
              36 months (−5%)
            </Button>
          </div>
          <div className='flex flex-1 min-w-[220px] gap-2 items-end'>
            <div className='flex-1'>
              <label className='text-sm text-muted-foreground mb-1 block'>Promo / unlock code</label>
              <Input
                value={promoInput}
                onChange={(e) => setPromoInput(e.target.value)}
                placeholder='Optional code'
                className='h-9'
              />
            </div>
            <Button type='button' variant='secondary' size='sm' disabled={plansLoading} onClick={handleApplyPromo}>
              {plansLoading && appliedPromoCode ? <Loader2 size={16} strokeWidth={1.5} className='animate-spin' /> : 'Apply'}
            </Button>
            {(catalogPromo || appliedPromoCode) && (
              <Button type='button' variant='ghost' size='sm' onClick={handleClearPromo}>
                Clear
              </Button>
            )}
          </div>
          {catalogPromo && (
            <p className='w-full text-sm text-success'>
              Code applied
              {catalogPromo.unlockPlanIds?.length > 0 ? ` — unlocks: ${catalogPromo.unlockPlanIds.join(', ')}` : ''}
              {catalogPromo.applyDiscount && catalogPromo.coupon?.percent_off
                ? ` — ${catalogPromo.coupon.percent_off}% off`
                : ''}
            </p>
          )}
          {promoMessage && <p className='w-full text-sm text-destructive'>{promoMessage}</p>}
        </CardContent>
      </Card>

      {plansLoading ? (
        <div className='flex items-center gap-2 text-muted-foreground'>
          <Loader2 size={16} strokeWidth={1.5} className='animate-spin' />
          Loading plans…
        </div>
      ) : (
        <div className='grid gap-4 md:grid-cols-2 xl:grid-cols-3 max-w-6xl'>
          {(plans || []).map((plan) => {
            const cta = planCta(plan);
            const checkoutable = cta === 'checkout' || cta === 'checkout_unlocked';
            const priceLabel = formatLicensePrice(plan, licensePrices, fteBand, termMonths);
            const bullets = buildPlanIncludeBullets(plan);
            const isCurrent = orgDetails?.planId === plan.id && orgDetails?.subscriptionStatus === 'active';

            return (
              <Card key={plan.id} className='bg-card text-card-foreground border-border flex flex-col'>
                <CardHeader>
                  <CardTitle className='flex items-baseline justify-between gap-2'>
                    <span className='flex flex-wrap items-center gap-2'>
                      {plan.name}
                      {plan.unlockedByPromo && (
                        <span className='rounded-full bg-success-bg px-2 py-0.5 text-xs font-medium text-success'>
                          Unlocked with code
                        </span>
                      )}
                    </span>
                    {priceLabel && (
                      <span className='text-base font-medium text-muted-foreground'>
                        {priceLabel}
                        <span className='text-xs'> / {termMonths === 36 ? '3 yr' : 'yr'}</span>
                      </span>
                    )}
                    {!priceLabel && plan.contactSales && (
                      <span className='text-sm font-normal text-muted-foreground'>Talk to sales</span>
                    )}
                  </CardTitle>
                  {plan.audience && <CardDescription>{plan.audience}</CardDescription>}
                </CardHeader>
                <CardContent className='flex flex-1 flex-col gap-4'>
                  <ul className='space-y-1 text-sm text-muted-foreground flex-1'>
                    {bullets.slice(0, 8).map((b) => (
                      <li key={b}>• {b}</li>
                    ))}
                  </ul>
                  {isCurrent ? (
                    <Button disabled variant='secondary'>
                      Current plan
                    </Button>
                  ) : checkoutable ? (
                    <Button
                      onClick={() => handleCheckout(plan)}
                      disabled={!fteBand}>
                      {convertTrial ? 'Upgrade & pay' : 'Choose plan'}
                    </Button>
                  ) : cta === 'contact_sales' ? (
                    <Button
                      variant='outline'
                      loading={contactingPlanId === plan.id}
                      disabled={contactingPlanId === plan.id}
                      onClick={() => handleContactSales(plan)}>
                      Contact sales
                    </Button>
                  ) : (
                    <Button disabled variant='secondary'>
                      Not currently available
                    </Button>
                  )}
                  {!fteBand && checkoutable && (
                    <p className='text-xs text-muted-foreground'>Select an FTE band above to continue.</p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {loadError && loadError !== 'plans_not_available' && (
        <p className='text-destructive text-sm'>{trialRegistrationErrorMessage(loadError, 'Could not load plans.')}</p>
      )}

      <div className='flex flex-wrap gap-2'>
        <Button asChild variant='outline'>
          <Link to='/'>Back to dashboard</Link>
        </Button>
        <Button
          variant='secondary'
          onClick={() =>
            handleContactSales({ id: null, name: 'general enquiry' })
          }>
          Talk to sales
        </Button>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  orgDetails: state.organisation?.details,
  isWhiteLabelApp: state?.whiteLabelAdmin?.whiteLabelId !== '0',
  plans: state.payment?.registrationPlans?.plans || [],
  fteBands: state.payment?.registrationPlans?.fteBands || [],
  licensePrices: state.payment?.registrationPlans?.licensePrices || null,
  plansLoading: state.payment?.registrationPlansLoading,
  plansError: state.payment?.registrationPlans?.error,
  catalogPromo: state.payment?.registrationPlans?.promo || null,
});

const mapDispatchToProps = (dispatch) => ({
  getRegistrationPlans: (promoCode) => dispatch(PAYMENTACTIONS.getRegistrationPlans(promoCode)),
  setPlanUpgradeSelection: (selection) => dispatch(PAYMENTACTIONS.setPlanUpgradeSelection(selection)),
  contactSales: (payload) => dispatch(PAYMENTACTIONS.contactSales(payload)),
  stripeCouponClear: () => dispatch(PAYMENTACTIONS.stripeCoupon(null)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PlansPage);
